import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { endpoints } from "@/api";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Building2, Plus, Trash2, MapPin, Pencil, X, ClipboardCheck } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "./ConfirmDialog";
import { CardSkeleton } from "./Skeletons";

export default function BranchesTab() {
  const { isRTL } = useI18n();
  const qc = useQueryClient();
  const { confirm, dialog } = useConfirm();
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", location: "" });

  const { data: overview } = useQuery({
    queryKey: ["mycafe-overview"], queryFn: endpoints.myCafeOverview,
  });
  const { data: branchList = [], isLoading } = useQuery({
    queryKey: ["mycafe-branches"], queryFn: endpoints.myCafeBranches,
  });
  // Merge overview stats (visit_count, avg_score, last_visit_date) into the branch list.
  const branches = branchList.map((b) => {
    const o = overview?.branches?.find((x) => x.id === b.id) || {};
    return { ...b, ...o };
  });

  const startNew = () => { setEditing("new"); setForm({ name: "", location: "" }); };
  const startEdit = (b) => { setEditing(b); setForm({ name: b.name, location: b.location || "" }); };
  const cancel = () => { setEditing(null); setForm({ name: "", location: "" }); };

  const save = useMutation({
    mutationFn: async () => {
      if (!form.name.trim()) throw new Error(isRTL ? "اسم الفرع مطلوب" : "Branch name required");
      if (editing === "new") return endpoints.myCafeCreateBranch(form);
      return endpoints.myCafeUpdateBranch(editing.id, form);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["mycafe-branches"] });
      qc.invalidateQueries({ queryKey: ["mycafe-overview"] });
      toast.success(isRTL ? "تم الحفظ" : "Saved");
      cancel();
    },
    onError: (e) => toast.error(e.message || "Error"),
  });

  const handleDelete = async (b) => {
    const ok = await confirm({
      title: isRTL ? `حذف "${b.name}"؟` : `Delete "${b.name}"?`,
      description: isRTL
        ? "زيارات الجودة المرتبطة بهذا الفرع ستبقى محفوظة، لكنها لن تكون مرتبطة بفرع."
        : "Visits linked to this branch will remain but lose their branch link.",
      destructive: true,
      confirmLabel: isRTL ? "نعم، احذف" : "Yes, delete",
    });
    if (!ok) return;
    await endpoints.myCafeDeleteBranch(b.id);
    qc.invalidateQueries({ queryKey: ["mycafe-branches"] });
    qc.invalidateQueries({ queryKey: ["mycafe-overview"] });
    toast.success(isRTL ? "تم الحذف" : "Deleted");
  };

  return (
    <div className="space-y-6" data-testid="mycafe-branches">
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-[#5C544F]">
          {isRTL ? "أضف فرعاً واحداً أو أكثر، وستظهر إحصاءات الزيارات لكل فرع تلقائياً." : "Add one or more branches. Visit stats appear automatically for each branch."}
        </p>
        {editing === null && (
          <Button onClick={startNew} className="bg-[#2A2421] text-[#F9F6F0]" data-testid="branch-add-btn">
            <Plus className="w-4 h-4 me-1.5" />
            {isRTL ? "إضافة فرع" : "Add branch"}
          </Button>
        )}
      </div>

      {editing !== null && (
        <div className="soft-card rounded-2xl p-6 space-y-4" data-testid="branch-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>{isRTL ? "اسم الفرع" : "Branch name"} *</Label>
              <Input
                data-testid="branch-name-input"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={isRTL ? "مثال: فرع الخبر" : "e.g. Khobar Branch"}
                className="mt-1.5 h-11"
              />
            </div>
            <div>
              <Label>{isRTL ? "العنوان / الحي (اختياري)" : "Location (optional)"}</Label>
              <Input
                data-testid="branch-loc-input"
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder={isRTL ? "حي العليا، الرياض" : "Al Olaya, Riyadh"}
                className="mt-1.5 h-11"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => save.mutate()} disabled={save.isPending} className="bg-[#C86240] text-white" data-testid="branch-save-btn">
              {save.isPending ? "..." : isRTL ? "حفظ" : "Save"}
            </Button>
            <Button onClick={cancel} variant="outline" data-testid="branch-cancel-btn">
              <X className="w-4 h-4 me-1.5" />
              {isRTL ? "إلغاء" : "Cancel"}
            </Button>
          </div>
        </div>
      )}

      {isLoading ? (
        <CardSkeleton rows={3} />
      ) : branches.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#2A2421]/15 p-12 text-center bg-[#F9F6F0]/50">
          <div className="w-16 h-16 mx-auto rounded-full bg-[#C86240]/10 flex items-center justify-center mb-4">
            <Building2 className="w-7 h-7 text-[#C86240]" />
          </div>
          <div className="text-[#2A2421] font-medium text-lg">{isRTL ? "لا توجد فروع بعد" : "No branches yet"}</div>
          <div className="text-sm text-[#5C544F] mt-1 max-w-md mx-auto">
            {isRTL ? "أضف أول فرع لتبدأ بإنشاء تقارير زيارات الجودة." : "Add your first branch to start creating quality visit reports."}
          </div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {branches.map((b) => {
            const s = b.avg_score;
            const color = s == null ? "text-[#5C544F]" : s >= 80 ? "text-emerald-600" : s >= 60 ? "text-amber-500" : "text-red-500";
            const last = b.last_visit_date ? new Date(b.last_visit_date) : null;
            return (
              <div key={b.id} className="soft-card rounded-2xl p-5 hover:shadow-md transition-shadow" data-testid={`branch-card-${b.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Building2 className="w-4 h-4 text-[#C86240] shrink-0" />
                      <div className={`text-lg ${isRTL ? "font-serif-ar" : "font-serif-en"} text-[#2A2421] truncate`}>{b.name}</div>
                    </div>
                    {b.location && (
                      <div className="flex items-center gap-1 text-xs text-[#5C544F]">
                        <MapPin className="w-3 h-3" />
                        {b.location}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button onClick={() => startEdit(b)} className="p-1.5 hover:bg-[#2A2421]/5 rounded-full" data-testid={`branch-edit-${b.id}`}>
                      <Pencil className="w-3.5 h-3.5 text-[#5C544F]" />
                    </button>
                    <button onClick={() => handleDelete(b)} className="p-1.5 hover:bg-red-50 rounded-full" data-testid={`branch-del-${b.id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-red-500" />
                    </button>
                  </div>
                </div>
                <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-lg bg-[#F9F6F0] p-2">
                    <div className={`text-2xl font-bold ${color}`}>{s ?? "—"}</div>
                    <div className="text-[10px] uppercase tracking-widest text-[#5C544F] mt-0.5">{isRTL ? "المتوسط" : "Avg"}</div>
                  </div>
                  <div className="rounded-lg bg-[#F9F6F0] p-2">
                    <div className="text-2xl font-bold text-[#2A2421]">{b.visit_count || 0}</div>
                    <div className="text-[10px] uppercase tracking-widest text-[#5C544F] mt-0.5 flex items-center justify-center gap-1">
                      <ClipboardCheck className="w-2.5 h-2.5" />
                      {isRTL ? "زيارة" : "Visits"}
                    </div>
                  </div>
                  <div className="rounded-lg bg-[#F9F6F0] p-2">
                    <div className="text-xs font-medium text-[#2A2421] mt-1">
                      {last ? last.toLocaleDateString(isRTL ? "ar-SA" : "en-GB", { month: "short", day: "numeric" }) : "—"}
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-[#5C544F] mt-0.5">{isRTL ? "آخر زيارة" : "Last"}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {dialog}
    </div>
  );
}
